/**
 * Modellserier — programmatisk sidfamilj på /modeller/$brand/$serie.
 *
 * Syftet är long tail-sökningar som "kabe royal", "polar svit", "adria alpina
 * pris" där märkessidan är för bred och fordonsannonsen för smal. Innehållet är
 * skrivet utifrån vad vi faktiskt ser i verkstaden och i inbyten i Kalix —
 * inga kopierade specifikationsblad. Prisintervall är våra observationer på
 * den svenska begagnatmarknaden och anges som spann, inte som utfästelser.
 */

import { MODELL_SERIER_2 } from "./modell-serier-2";


export interface ModellSerie {
  brandSlug: string;
  brandName: string;
  category: "husbil" | "husvagn";
  slug: string;
  name: string;
  h1: string;
  metaTitle: string;
  metaDesc: string;
  keyword: string;
  intro: string;
  /** Vanliga planlösningar i serien. */
  layouts: string[];
  perfectFor: string[];
  strengths: string[];
  weaknesses: string[];
  /** Vinterduglighet i norrländskt klimat. */
  winter: string;
  priceNew: string;
  priceUsed: string;
  /** Fel vi återkommande åtgärdar på serien. */
  vanligaFel: string[];
  /** Vad vi kontrollerar innan vi köper in eller säljer ett exemplar. */
  checklist: string[];
  /** Fördjupande stycken — ger sidan egen tyngd i kvalitetsgrinden. */
  djup: string[];
  faq: { q: string; a: string }[];
}

const MODELL_SERIER_1: ModellSerie[] = [
  {
    brandSlug: "kabe",
    brandName: "Kabe",
    category: "husvagn",
    slug: "royal",
    name: "Kabe Royal",
    h1: "Kabe Royal — vagnen som klarar norrländsk vinter",
    metaTitle: "Kabe Royal begagnad: pris, planlösningar & vad vi kollar",
    metaDesc:
      "Kabe Royal begagnat 2014–2023: prisspann per årsmodell, vanliga planlösningar, vad vi ser på fukt och golvvärme samt vår 12-punkters kontroll.",
    keyword: "kabe royal",
    intro:
      "Royal är Kabes tyngsta serie och den vagn vi oftast rekommenderar till den som faktiskt ska stå ute i november. Isolering, golvvärme och dubbelt golv gör skillnad när det är minus 20 i Kalix.",
    layouts: [
      "Enkelsängar bak med våtrum i mitten — populärast hos par som campar långt",
      "Långbädd i sidan (t.ex. 780-modellerna) för dig som vill ha bäddad säng dygnet runt",
      "Familjeplanlösning med våningssäng bak och rundsittgrupp fram",
    ],
    perfectFor: [
      "Året-runt-camping i Norrbotten där vagnen står uppställd i kyla i veckor",
      "Par som prioriterar sovkomfort och gott om garderobsmeter",
      "Den som vill ha hög andrahandsvärde och lätt att sälja vidare",
    ],
    strengths: [
      "Dubbelt golv med varmluft i golvkanalerna — vattnet fryser inte lika lätt",
      "AGS-systemet ventilerar golvet och torkar ut fukt aktivt",
      "Kraftig chassiuppbyggnad tål grusväg och tjälskott",
      "Starkast andrahandsvärde av de vagnar vi handlar med",
    ],
    weaknesses: [
      "Tjänstevikten är hög — kontrollera dragbilens kapacitet före köp",
      "Dyrast i inköp både nytt och begagnat i sitt storleksspann",
      "Vissa reservdelar är märkesspecifika och tar några dagar att få hem",
    ],
    winter:
      "Royal är byggd för vinterbruk från fabrik. Vi ser att kupén håller +20 grader vid -25 utomhus med normal gasol- eller elvärme, förutsatt att golvventilerna är öppna och att tilluften bak inte är blockerad av förvaring.",
    priceNew: "Från cirka 620 000 kr beroende på längd och utrustning",
    priceUsed:
      "2014–2016: 240 000–360 000 kr · 2017–2019: 350 000–480 000 kr · 2020–2023: 480 000–720 000 kr",
    vanligaFel: [
      "Fuktvärden kring bakre hörnstolpar på vagnar över 8 år — mät alltid i 12 punkter",
      "Läckage vid takluckans list när originalsilikonet passerat 6–8 år",
      "Trög gångjärnsfunktion på tunga garderobsdörrar efter mycket grusväg",
      "Bromsjustering som glappat efter första 5 000 milen",
    ],
    checklist: [
      "Fuktmätning i 12 punkter — under 15 % är grönt, över 20 % stoppar affären",
      "Kontroll av AGS-kanaler så de inte är igensatta av isolering eller skräp",
      "Provkörning med lastad vagn för att känna av bromsverkan och sväng",
      "Genomgång av gasolsystem med täthetsprov och datum på slangar",
      "Kontroll av boggiaxlarnas gummifjädring på modeller över 10 år",
    ],
    djup: [
      "Skillnaden mellan Royal och de enklare Kabe-serierna märks tydligast efter tredje vinternatten. Golvvärmen håller golvytan runt 18–20 grader även när det är -25 ute, vilket gör att fukt inte kondenserar mot kalla ytor. Det är också anledningen till att vi ser lägre fuktvärden på Royal-vagnar i samma ålder som billigare vagnar: konstruktionen torkar ut sig själv i stället för att samla vatten i skarvarna.",
      "Vikten är den vanligaste fällan. En Royal på 780 cm ligger ofta på 1 800–2 000 kg tjänstevikt och totalvikt runt 2 000–2 500 kg. Med B-körkort och en dragbil på 2 000 kg tjänstevikt blir det snabbt för tight, och då behövs utökad B eller BE. Vi går igenom registreringsbeviset tillsammans innan vi skriver kontrakt så att ekipaget blir lagligt lastat med vatten, gasol och packning ombord.",
      "På begagnatmarknaden är Royal en av få vagnar som håller värdet. En vagn köpt för 550 000 kr som fem år gammal ligger ofta kvar kring 380 000–420 000 kr, alltså cirka 6–8 procents årlig värdeminskning mot 10–12 procent för volymvagnar. Det gör att totalkostnaden per campingnatt ofta blir lägre trots högre inköpspris — särskilt om du campar mer än 40 nätter per år.",
      "Vi hanterar Royal-vagnar löpande i verkstaden i Kalix och har mätprotokoll från flera hundra fukttester. Det ger en ganska tydlig bild: de vagnar som får sitt tätningsunderhåll vid 6 år och sedan vartannat år klarar sig i regel utan fuktskada, medan de som hoppar över underhållet visar förhöjda värden runt 9–11 års ålder.",
    ],
    faq: [
      {
        q: "Vilket år ska man välja på en begagnad Kabe Royal?",
        a: "Från 2017 och framåt får du modernare el, bättre LED-belysning och nyare AGS-generation. Är budgeten under 350 000 kr fungerar 2014–2016 utmärkt om fuktprotokollet är rent.",
      },
      {
        q: "Klarar Royal att stå uppställd hela vintern?",
        a: "Ja, det är precis vad serien är byggd för. Se till att golvventilerna är öppna, att taket skottas vid mycket snö och att batteriet hålls laddat.",
      },
      {
        q: "Behöver jag BE-körkort till en Kabe Royal?",
        a: "Ofta ja på de längre modellerna. Vi räknar på totalvikt plus dragbilens tjänstevikt innan köp så att du vet exakt vad som gäller.",
      },
    ],
  },
  {
    brandSlug: "kabe",
    brandName: "Kabe",
    category: "husvagn",
    slug: "smaragd",
    name: "Kabe Smaragd",
    h1: "Kabe Smaragd — vinterkvalitet i mindre format",
    metaTitle: "Kabe Smaragd begagnad: pris per årsmodell & planlösningar",
    metaDesc:
      "Smaragd är Kabes mellanserie: lägre vikt än Royal, samma vinterfilosofi. Prisspann 2013–2023, planlösningar, vanliga fel och vår kontroll före köp.",
    keyword: "kabe smaragd",
    intro:
      "Smaragd ligger mitt i Kabes program: du får vinterbygget och dubbla golvet men i ett format som fler dragbilar orkar med. Det är vår vanligaste rekommendation till den som byter upp sig från en enklare vagn.",
    layouts: [
      "470-modellen med långbädd — den mest efterfrågade begagnatvagnen vi säljer",
      "Enkelsängar med våtrum bak för par som vill ha eget utrymme",
      "Kompakt familjeplanlösning på 560–600 cm",
    ],
    perfectFor: [
      "Par som campar 20–60 nätter per år i blandat väder",
      "Dragbilar med 1 700–2 000 kg dragvikt där Royal blir för tung",
      "Den som vill ha Kabes vinteregenskaper utan Royals prislapp",
    ],
    strengths: [
      "Cirka 150–300 kg lägre tjänstevikt än motsvarande Royal",
      "Dubbelt golv även här — vattentanken sitter frostskyddad",
      "Enkel att sälja vidare, efterfrågan på 470-modellen är konstant",
      "Bra utrymmesutnyttjande i förhållande till ytterlängden",
    ],
    weaknesses: [
      "Något enklare inredningsdetaljer och färre eluttag än Royal",
      "Mindre färskvattentank gör att man fyller oftare vid långtidscamping",
      "Begränsat förrådsutrymme i de kortaste planlösningarna",
    ],
    winter:
      "Smaragd klarar normal svensk vinter utan problem. Vid längre perioder under -20 grader rekommenderar vi extra tätning kring dörren och att man inte blockerar varmluftskanalerna med packning under sängen.",
    priceNew: "Från cirka 480 000 kr",
    priceUsed:
      "2013–2015: 180 000–260 000 kr · 2016–2019: 250 000–380 000 kr · 2020–2023: 380 000–560 000 kr",
    vanligaFel: [
      "Fukt i främre hörn vid dragstången på vagnar äldre än 9 år",
      "Slitna dörrtätningar som ger drag vid -15 och kallare",
      "Kylskåpets ventilationsgaller som glappar efter grusvägskörning",
      "Nedsatt batterikapacitet när vagnen stått oladdad över vintern",
    ],
    checklist: [
      "Fuktmätning i 12 punkter med särskilt fokus på framvägg och dragstångsinfästning",
      "Kontroll av att dubbelgolvets dräneringshål är öppna",
      "Test av samtliga 12 V-uttag och LED-armaturer",
      "Kontroll av däckens tillverkningsvecka — vi byter alltid vid 6 år oavsett mönsterdjup",
      "Funktionstest av värmepanna och varmvatten i minst 30 minuter",
    ],
    djup: [
      "Smaragd är den serie där vi ser störst skillnad mellan välskötta och vanskötta exemplar. Två vagnar från samma år kan skilja 60 000–80 000 kr i värde beroende enbart på fuktprotokoll och servicehistorik. Därför är dokumentationen viktigare än mätarställningen: en vagn med kvitton på tätningsservice vart annat år är i praktiken alltid en bättre affär.",
      "Den populära 470-modellen med långbädd är vår mest efterfrågade begagnatvagn. Efterfrågan gör att prisspannet är smalt: 2018 års exemplar i gott skick byter ägare kring 330 000–370 000 kr året runt, med bara 5–10 procents säsongsvariation mellan mars och oktober. Den som säljer på våren får normalt bäst betalt, den som köper i november får bäst förhandlingsläge.",
      "Viktmässigt ligger Smaragd typiskt på 1 350–1 650 kg tjänstevikt beroende på längd, vilket gör att många familjebilar räcker till. Vi räknar alltid med minst 150 kg last utöver tjänstevikten: 40 liter vatten väger 40 kg, två gasoltuber cirka 30 kg och normal packning 80–100 kg. Det är den summan som avgör om ekipaget är lagligt, inte bara siffran i broschyren.",
      "I verkstaden ser vi att Smaragd-vagnar från 2016 och framåt har färre elrelaterade fel än äldre årgångar, framför allt tack vare övergången till LED och nyare centralenhet. Det gör 2016 till en praktisk brytpunkt när man jämför två annars likvärdiga objekt.",
    ],
    faq: [
      {
        q: "Vad skiljer Smaragd från Royal?",
        a: "Royal har tjockare isolering, mer utrustning och högre vikt. Smaragd har samma grundfilosofi med dubbelt golv men är lättare och billigare, vilket passar fler dragbilar.",
      },
      {
        q: "Är Kabe Smaragd 470 med långbädd svår att få tag på?",
        a: "Ja, den säljs oftast inom några veckor. Lägg en prisvakt hos oss så hör vi av oss innan vagnen annonseras ut brett.",
      },
      {
        q: "Hur mycket drar en Smaragd i bränsle på dragbilen?",
        a: "Räkna med 2–4 liter extra per 100 km jämfört med solokörning, beroende på hastighet och vind. I 80 km/h blir merförbrukningen märkbart lägre än i 90.",
      },
    ],
  },
  {
    brandSlug: "polar",
    brandName: "Polar",
    category: "husvagn",
    slug: "svit",
    name: "Polar Svit",
    h1: "Polar Svit — nordisk vagn med rejält utrymme",
    metaTitle: "Polar Svit begagnad: pris, planlösning & vinteregenskaper",
    metaDesc:
      "Polar Svit 620–770: prisspann per årsmodell, vad planlösningen passar för, vanliga fel vi åtgärdar och checklistan vi kör före varje köp.",
    keyword: "polar svit",
    intro:
      "Polar Svit är för många den prisvärda vägen till en riktig vintervagn. Du får nordisk isolering och rymlig planlösning för mindre pengar än premiumsegmentet, och delarna är lätta att få tag på.",
    layouts: [
      "Svit 620 — kompakt parvagn med enkelsängar",
      "Svit 700 med rundsittgrupp och separat sovrum bak",
      "Svit 770 för familjer som vill ha både våningssäng och stor sittgrupp",
    ],
    perfectFor: [
      "Familjer som vill ha yta per krona",
      "Säsongscamping där vagnen står på samma plats maj–september",
      "Den som vill ha nordisk vinterstandard men har budget under 400 000 kr",
    ],
    strengths: [
      "Prisvärd i förhållande till ytan — ofta 15–25 % billigare än premiumvagn i samma längd",
      "Isolering anpassad för svenskt klimat, inte centraleuropeiskt",
      "Enkel och robust teknik som de flesta verkstäder kan serva",
      "Bra planlösningar för familj med två barn",
    ],
    weaknesses: [
      "Inredningsdetaljer i enklare utförande än Kabe",
      "Snabbare värdeminskning, cirka 9–12 % per år de första fem åren",
      "Vissa årgångar har enkelgolv — kontrollera specifikationen noga",
    ],
    winter:
      "Svit fungerar bra ner mot -20 grader med normal uppvärmning. För helårsboende rekommenderar vi kompletterande golvisolering och kjol runt vagnen, vilket vi monterar för 8 000–15 000 kr beroende på storlek.",
    priceNew: "Från cirka 420 000 kr",
    priceUsed:
      "2012–2015: 130 000–210 000 kr · 2016–2019: 200 000–310 000 kr · 2020–2023: 300 000–470 000 kr",
    vanligaFel: [
      "Kondens i takluckor vid dåligt ventilerad vintercamping",
      "Fukt kring bakre stänkskärmar på vagnar äldre än 8 år",
      "Trötta stödben som kärvar efter fem–sex säsonger utan smörjning",
      "Slitna hjullager på vagnar som gått mer än 4 000 mil",
    ],
    checklist: [
      "Fuktmätning i 12 punkter, extra kontroll runt bakvagn och stänkskärmar",
      "Kontroll av om vagnen har enkelt eller dubbelt golv — påverkar vinterduglighet",
      "Genomgång av stödben, domkraftsfästen och draganordning",
      "Test av värmesystem, varmvatten och samtliga gaslågor",
      "Kontroll av tak och lister efter mikrosprickor i tätmassan",
    ],
    djup: [
      "Polar tillverkas för nordiska förhållanden, vilket i praktiken betyder tjockare väggar och ett värmesystem dimensionerat för minusgrader snarare än för medelhavsvår. Skillnaden mot en centraleuropeisk vagn i samma prisklass märks tydligast i mars: Polar-vagnen håller jämn temperatur medan importvagnen ofta får kalla golv och kondens vid fönsternischerna.",
      "Prismässigt är Svit en volymvagn, och det syns i värdekurvan. En vagn köpt för 430 000 kr ligger efter fem år normalt kring 250 000–290 000 kr. Det är sämre än Kabe men bättre än många importmärken, och skillnaden i inköpspris gör ändå att totalkostnaden ofta landar likvärdigt över en sjuårsperiod om du campar 30–50 nätter per år.",
      "När vi köper in Svit-vagnar tittar vi extra på golvkonstruktionen. Vissa årgångar och utföranden har enkelt golv, och de vagnarna ska inte marknadsföras som helårsvagnar utan kompletterande isolering. Det är den enskilt vanligaste missuppfattningen vi rätar ut i butiken i Kalix.",
      "Reservdelsläget är en av seriens styrkor. Standardkomponenter som luckor, ventiler, kylskåpsgaller och stödben finns oftast hemma eller inom 3–7 dagar, vilket gör att en reparation sällan står stilla i veckor. Det är värt att väga in för den som campar mycket under sommarsäsongen och inte vill förlora semesterveckor på väntetid.",
    ],
    faq: [
      {
        q: "Är Polar Svit en riktig vintervagn?",
        a: "Ja, med dubbelt golv och rätt ventilation klarar den svensk vinter. Kontrollera alltid vilken golvkonstruktion just det exemplaret har.",
      },
      {
        q: "Vad kostar det att göra Svit redo för helårsbruk?",
        a: "Kjol, extra golvisolering och tätningsöversyn landar normalt på 8 000–15 000 kr hos oss beroende på vagnens storlek och skick.",
      },
      {
        q: "Hur står sig Polar mot Kabe på begagnatmarknaden?",
        a: "Kabe håller värdet bättre, men Polar kostar mindre att köpa. Räknat per campingnatt över sju år hamnar de ofta nära varandra.",
      },
    ],
  },
  {
    brandSlug: "adria",
    brandName: "Adria",
    category: "husvagn",
    slug: "alpina",
    name: "Adria Alpina",
    h1: "Adria Alpina — Adrias flaggskepp bland husvagnar",
    metaTitle: "Adria Alpina pris begagnad: årsmodeller, planlösning & fel",
    metaDesc:
      "Adria Alpina begagnat: prisspann per årsmodell, planlösningar med långbädd och ö-säng, vanliga fel vi åtgärdar samt vår kontroll före köp.",
    keyword: "adria alpina pris",
    intro:
      "Alpina är Adrias premiumvagn och den serie som ligger närmast nordisk standard i märkets program. Utrustningsnivån är hög och planlösningarna generösa — men vinterduglighet varierar med utförande.",
    layouts: [
      "Ö-säng bak med stort våtrum — populärt hos par som campar länge",
      "Långbädd i sidan med rejäl garderob",
      "Familjeplanlösning med separat sovrum och våningssäng",
    ],
    perfectFor: [
      "Par som vill ha hotellkänsla och gott om köksyta",
      "Sommar- och trekvartsårscamping i södra och mellersta Norrland",
      "Den som vill ha mycket utrustning per krona jämfört med tyska premiummärken",
    ],
    strengths: [
      "Hög utrustningsnivå redan från fabrik",
      "Modern inredningsdesign med god ljusinsläpp",
      "Bra köksyta och kylvolym för längre resor",
      "Stort europeiskt återförsäljarnät ger snabb delförsörjning, ofta 3–7 dagar",
    ],
    weaknesses: [
      "Vinterpaket krävs för fullgod norrländsk vinterdrift",
      "Värdeminskning kring 12–14 % per år de första fyra åren",
      "Tyngre utföranden kräver noggrann viktkontroll mot dragbilen",
    ],
    winter:
      "I standardutförande klarar Alpina ner mot -10 till -12 grader utan besvär. Ska vagnen stå ute i Norrbotten under djupvinter rekommenderar vi vinterpaket med extra golvisolering, en åtgärd som normalt kostar 8 000–15 000 kr.",
    priceNew: "Från cirka 520 000 kr",
    priceUsed:
      "2014–2016: 170 000–250 000 kr · 2017–2019: 240 000–360 000 kr · 2020–2023: 350 000–540 000 kr",
    vanligaFel: [
      "Kondens vid fönsternischer under kalla nätter i standardutförande",
      "Fuktvärden runt takluckor på vagnar över 7 år",
      "Slitna lister vid serviceluckor efter mycket grusväg",
      "Trög funktion på vattenpump när filtret inte bytts på flera säsonger",
    ],
    checklist: [
      "Fuktmätning i 12 punkter med särskild kontroll runt takluckor",
      "Kontroll av om vinterpaket finns monterat — påverkar priset med 15 000–25 000 kr",
      "Genomgång av vattensystem inklusive pump, filter och frostventil",
      "Test av samtliga fönsterhissar och myggnät",
      "Kontroll av däckålder och reservhjulets skick",
    ],
    djup: [
      "Alpina är den Adria-serie där utrustningen känns dyrare än prislappen. Du får ofta samma köksstandard och sovkomfort som i märken som kostar 100 000–150 000 kr mer nytt. Kompromissen ligger i isolering och detaljhållfasthet: vagnen är byggd i en 30 mm sandwichvägg med gelcoat, vilket är repbeständigt men inte lika tjockt som nordiska helårsvagnar.",
      "Det gör att vinterpaketets närvaro är den enskilt viktigaste faktorn när vi prissätter ett begagnat exemplar. En Alpina med vinterpaket från 2019 kan ligga 20 000–30 000 kr över en identisk vagn utan, och den skillnaden håller sig genom hela ägandetiden eftersom nästa köpare gör samma bedömning.",
      "Adria är ett av Sveriges mest registrerade märken, vilket ger en fördel på begagnatmarknaden: utbudet är stort och du kan vänta in rätt planlösning i stället för att ta första bästa. Nackdelen är att värdeminskningen blir brantare — räkna med att en vagn köpt för 520 000 kr ligger kring 300 000–340 000 kr efter fyra år.",
      "I verkstaden ser vi att Alpina-vagnar sköter sig väl så länge tätningsunderhållet görs. De problem som dyker upp är sällan konstruktionsfel utan uteblivet underhåll: silikon som passerat sin livslängd på 6–8 år, filter som aldrig bytts och batterier som stått urladdade genom en hel vinter.",
    ],
    faq: [
      {
        q: "Passar Adria Alpina för vintercamping i Norrbotten?",
        a: "Med vinterpaket ja, i standardutförande blir det kallt kring golv och fönsternischer vid längre perioder under -15.",
      },
      {
        q: "Vad kostar en begagnad Adria Alpina?",
        a: "Vanliga spann är 240 000–360 000 kr för 2017–2019 och 350 000–540 000 kr för 2020–2023, beroende på planlösning och om vinterpaket finns.",
      },
      {
        q: "Är Alpina bättre än Adria Adora?",
        a: "Alpina ligger ett steg över i utrustning och inredningskänsla. Adora är lättare och billigare och räcker gott för sommarcamping.",
      },
    ],
  },
  {
    brandSlug: "adria",
    brandName: "Adria",
    category: "husvagn",
    slug: "adora",
    name: "Adria Adora",
    h1: "Adria Adora — mångsidig vagn för familjen",
    metaTitle: "Adria Adora begagnad: pris, planlösningar och vad du bör kolla",
    metaDesc:
      "Adria Adora begagnat 2015–2023: prisspann, planlösningar för familj och par, vanliga fel i verkstaden och vår checklista före köp.",
    keyword: "adria adora",
    intro:
      "Adora är Adrias mest sålda husvagnsserie i Sverige och sitter mitt i sortimentet: lagom vikt, många planlösningar och ett pris som gör den till en vanlig förstavagn.",
    layouts: [
      "Enkelsängar bak — enklast att bädda och sköta",
      "Familjeplanlösning med våningssäng i sidan",
      "Kompakt planlösning kring 5,5 meter för mindre dragbilar",
    ],
    perfectFor: [
      "Förstagångsköpare som campar 15–40 nätter per säsong",
      "Familjer med barn där två sovplatser extra behövs",
      "Dragbilar med begränsad dragvikt kring 1 500–1 800 kg",
    ],
    strengths: [
      "Lätt i förhållande till invändig yta",
      "Prisvärd med hyfsad utrustning i standardutförande",
      "Många exemplar på marknaden ger bra förhandlingsläge",
      "Enkel att köra och parkera i de kortare planlösningarna",
    ],
    weaknesses: [
      "Enklare isolering — inte förstahandsval för djupvinter",
      "Mindre förvaringsvolym än nordiska vagnar i samma längd",
      "Inredningsytor tål mindre slitage än premiumserierna",
    ],
    winter:
      "Adora fungerar bra under vår och höst men är i grunden en trekvartsårsvagn. För camping under -10 grader behövs kompletterande isolering och noggrann ventilering för att undvika kondens.",
    priceNew: "Från cirka 390 000 kr",
    priceUsed:
      "2015–2017: 130 000–195 000 kr · 2018–2020: 185 000–280 000 kr · 2021–2023: 270 000–410 000 kr",
    vanligaFel: [
      "Kondens och mögelpunkter bakom dynor vid dålig ventilation",
      "Läckande takluckor på vagnar äldre än 7 år",
      "Trasiga fönsterhissar som inte håller rutan uppe",
      "Slitna dörrlås och gångjärn på flitigt använda familjevagnar",
    ],
    checklist: [
      "Fuktmätning i 12 punkter — vi lyfter alltid dynorna och kontrollerar bakom",
      "Test av alla fönster, luckor och myggnät",
      "Kontroll av kylskåpets funktion i både 12 V, 230 V och gasoldrift",
      "Genomgång av bromsar och hjullager, särskilt över 3 000 mil",
      "Kontroll av tätningar kring takluckor och serviceluckor",
    ],
    djup: [
      "Adora är den vagn vi oftast ser som steg ett in i husvagnslivet. Den är lätt nog för de flesta familjebilar, tjänstevikten ligger vanligen mellan 1 100 och 1 500 kg beroende på längd, och prisbilden gör att man kan prova livsstilen utan att binda upp en halv miljon. Det gör också att utbudet av begagnade exemplar är stort året runt.",
      "Baksidan av populariteten är att skicket varierar mycket. Familjevagnar används hårt: barn, hundar och packning sliter på inredning och tätningar. Vi lägger därför extra tid på fuktprotokollet och på att lyfta varje dyna — de flesta problem vi hittar syns inte förrän man tittar bakom och under.",
      "Prismässigt tappar Adora ungefär 10–12 procent per år de första fem åren. En vagn köpt ny för 390 000 kr ligger typiskt kring 230 000–260 000 kr efter fem år. Köper du i stället ett fyra år gammalt exemplar tar du en betydligt flackare del av kurvan, vilket är den enskilt största besparingen en förstagångsköpare kan göra.",
      "För norrländsk användning rekommenderar vi Adora som vår-, sommar- och höstvagn. Vill du ställa ut vagnen i november behöver du antingen investera i isolering och kjol eller titta på en nordisk serie som Kabe Smaragd eller Polar Svit i stället — det blir ofta en billigare väg till samma komfort.",
    ],
    faq: [
      {
        q: "Är Adria Adora bra som första husvagn?",
        a: "Ja. Låg vikt, hanterbart pris och stort utbud gör den till ett tryggt förstaval, särskilt för sommar- och höstcamping.",
      },
      {
        q: "Hur mycket väger en Adria Adora?",
        a: "Tjänstevikten ligger vanligtvis mellan 1 100 och 1 500 kg beroende på längd. Räkna med 150 kg last utöver det.",
      },
      {
        q: "Kan man vintercampa med Adora?",
        a: "Under kortare perioder ja, men vagnen är byggd för trekvartsår. Ska den stå ute hela vintern rekommenderar vi en nordisk serie.",
      },
    ],
  },
  {
    brandSlug: "knaus",
    brandName: "Knaus",
    category: "husvagn",
    slug: "sudwind",
    name: "Knaus Südwind",
    h1: "Knaus Südwind — tysk byggkvalitet i mellanklass",
    metaTitle: "Knaus Südwind begagnad: pris, planlösningar & vanliga fel",
    metaDesc:
      "Knaus Südwind begagnat: prisspann per årsmodell, planlösningar, vad tysk byggstandard betyder i svensk vinter och vår kontroll före köp.",
    keyword: "knaus südwind",
    intro:
      "Südwind är Knaus mest etablerade husvagnsserie och en klassiker på europeiska campingplatser. Byggkvaliteten är genomgående hög, men serien är dimensionerad för centraleuropeiskt klimat snarare än norrländsk vinter.",
    layouts: [
      "Enkelsängar bak med mittvåtrum",
      "Fransk säng i hörnet med rundsittgrupp fram",
      "Familjelayout med våningssäng och stor sittgrupp",
    ],
    perfectFor: [
      "Långresor i Europa där hög körkomfort och lätt vikt betyder mycket",
      "Par som prioriterar snickeridetaljer och materialkänsla",
      "Sommar- och höstcamping i Sverige",
    ],
    strengths: [
      "Genomarbetad snickeri- och beslagkvalitet",
      "Låg vikt i förhållande till utrustningsnivå",
      "God köregenskap tack vare balanserat chassi",
      "Stort utbud av reservdelar i hela Europa",
    ],
    weaknesses: [
      "Isolering anpassad för mildare klimat än Norrbotten",
      "Kondensrisk vid fönsternischer under kalla nätter",
      "Färre återförsäljare i norra Sverige gör service mer planerad",
    ],
    winter:
      "Südwind hanterar temperaturer ner mot -8 till -10 grader väl. Vid kallare väder behöver du komplettera med golvisolering, kjol och aktiv ventilation för att hålla fukten borta.",
    priceNew: "Från cirka 400 000 kr",
    priceUsed:
      "2014–2016: 120 000–190 000 kr · 2017–2019: 180 000–280 000 kr · 2020–2023: 270 000–430 000 kr",
    vanligaFel: [
      "Fukt vid framvägg efter långvarig regnkörning utan tätningsöversyn",
      "Kondens under madrasser vid vinterbruk utan ventilationsgaller",
      "Slitna gasfjädrar i sängbottnar efter 6–8 år",
      "Åldrade tätlister runt dörr och serviceluckor",
    ],
    checklist: [
      "Fuktmätning i 12 punkter med extra fokus på framvägg",
      "Kontroll av ventilation under sängbotten och bakom dynor",
      "Test av värmesystem under minst 30 minuter i drift",
      "Genomgång av draganordning, bromsar och stödben",
      "Kontroll av däckens tillverkningsvecka — byt vid 6 år",
    ],
    djup: [
      "Knaus bygger med hög precision, och det märks i detaljerna: luckor som sitter kvar i läge efter tusen mil grusväg, gångjärn som inte glappar och en inredning som håller formen. För den som kör mycket och långt är det en påtaglig kvalitetsskillnad mot billigare volymvagnar.",
      "Klimatanpassningen är dock den punkt vi alltid tar upp i butiken. Südwind är konstruerad för ett klimat där -5 grader är kallt, inte -25. Det betyder inte att den är olämplig i Sverige, men att vinterbruk kräver aktiva åtgärder: kjol runt vagnen, extra golvisolering och kontrollerad ventilation så att fukten transporteras ut i stället för att kondensera på kalla ytor.",
      "På begagnatmarknaden ligger Südwind ofta 10–20 procent under en nordisk vagn i samma ålder och längd. Det gör den intressant för den som campar maj–september och vill lägga pengarna på utrustning i stället för på isolering man inte utnyttjar. Räkna med en värdeminskning kring 10–12 procent per år de första fem åren.",
      "Vi servar Knaus löpande i Kalix. Delar som lister, ventiler och lås tar normalt 3–10 dagar att få hem, något längre än för de nordiska märkena, så för akuta fel mitt i semestern är framförhållning värdefull. Vi lagerhåller de vanligaste förbrukningsdelarna för att korta väntetiden.",
    ],
    faq: [
      {
        q: "Går det att vintercampa med Knaus Südwind?",
        a: "I korta perioder ja, men serien är byggd för mildare klimat. För helårsbruk i Norrbotten rekommenderar vi en nordisk vagn.",
      },
      {
        q: "Hur står sig Südwind mot Adria Adora?",
        a: "Knaus har generellt bättre snickeri och beslag, Adria har ofta mer utrustning för pengarna. Båda är trekvartsårsvagnar i grunden.",
      },
      {
        q: "Vad kostar service på en Knaus hos er?",
        a: "En normal årsservice med fukttest, gasoltäthetsprov och genomgång ligger i samma nivå som för övriga märken. Vi lämnar alltid pris innan arbetet påbörjas.",
      },
    ],
  },
  {
    brandSlug: "adria",
    brandName: "Adria",
    category: "husbil",
    slug: "matrix",
    name: "Adria Matrix",
    h1: "Adria Matrix — halvintegrerad med smart planlösning",
    metaTitle: "Adria Matrix begagnad: pris per årsmodell och vanliga fel",
    metaDesc:
      "Adria Matrix begagnat: prisspann 2016–2023, planlösningar med nedsänkbar säng, vikt och körkortsfrågan samt vår kontroll före köp.",
    keyword: "adria matrix",
    intro:
      "Matrix är Adrias halvintegrerade serie och en av de vanligaste husbilarna i vårt inbyte. Den kombinerar nedsänkbar säng, fast bädd bak och ett garage som räcker till två cyklar.",
    layouts: [
      "Enkelsängar bak med garage under — mest flexibla planlösningen",
      "Fransk säng bak med större våtrum",
      "Nedsänkbar dubbelsäng fram som ger fyra fullvärdiga sovplatser",
    ],
    perfectFor: [
      "Par som reser långt och vill ha garage för cyklar eller utrustning",
      "Familjer med två barn där den nedsänkbara sängen används dagligen",
      "Den som vill ha modern utrustning under 4 000 mm totalhöjd",
    ],
    strengths: [
      "Effektivt utnyttjad planlösning med både fast säng och garage",
      "Fiat Ducato- eller Citroën-chassi ger enkel service i hela Europa",
      "Bra isolering för trekvartsår med god värmefördelning",
      "Prisvärd jämfört med tyska halvintegrerade i samma storlek",
    ],
    weaknesses: [
      "Totalvikten ligger ofta nära 3 500 kg — lastutrymmet kan bli begränsat",
      "Vinterpaket behövs för djupvinterbruk i Norrbotten",
      "Gelcoat-ytan är repbeständig men känslig för hårda smällar",
    ],
    winter:
      "Med vinterpaket och dubbelt golv fungerar Matrix bra ner mot -15 till -20 grader. I standardutförande blir förarhytten den svaga punkten — isolerande hyttgardiner gör stor skillnad och kostar en bråkdel av allt annat.",
    priceNew: "Från cirka 850 000 kr",
    priceUsed:
      "2016–2018: 480 000–650 000 kr · 2019–2021: 620 000–830 000 kr · 2022–2023: 800 000–1 050 000 kr",
    vanligaFel: [
      "Läckage vid takluckor och solpanelfästen efter 6–8 år",
      "Slitna gasfjädrar på garagelucka och sänglyft",
      "Batterikapacitet som sjunkit efter flera vintrar utan underhållsladdning",
      "Fuktvärden i garagegolvet där blöt utrustning förvarats",
    ],
    checklist: [
      "Fuktmätning i 12 punkter inklusive garage och hjulhus",
      "Kontroll av totalvikt mot faktisk lastvikt — vi väger bilen vid behov",
      "Genomgång av kamrem eller kamkedja enligt chassitillverkarens intervall",
      "Funktionstest av sänglyft, markis och samtliga 12 V-kretsar",
      "Kontroll av däckens tillverkningsvecka och CP-märkning",
    ],
    djup: [
      "Matrix löser det som många husbilsköpare kämpar med: att få både fast säng och riktigt garage utan att bilen blir längre än att den går att parkera. Planlösningen med enkelsängar bak och lastutrymme under är den vi säljer flest av, eftersom två cyklar, campingmöbler och ett grillbord får plats utan att någon behöver bädda om varje kväll.",
      "Viktfrågan är den viktigaste detaljen vid köp. Många Matrix-modeller har totalvikt 3 500 kg och tjänstevikt kring 3 000–3 200 kg, vilket lämnar 300–500 kg last. Två personer, full vattentank på 100 liter, gasol och packning äter snabbt upp den marginalen. Vi går igenom siffrorna och väger vid behov, för en överlastad husbil är både en försäkringsfråga och en trafiksäkerhetsfråga.",
      "Chassivalet gör underhållet enkelt. Ducato-baserade bilar kan servas hos vilken verkstad som helst i Europa, och delar finns överallt. Det är en av anledningarna till att Adria hållit sig så väl på begagnatmarknaden trots en värdeminskning kring 12–14 procent per år de första fyra åren.",
      "För norrländsk användning är förarhytten det stora värmeläckaget. En isolerande hyttgardin och mattor på golvet gör mer för komforten än de flesta dyrare åtgärder. Ska bilen användas hela vintern rekommenderar vi utöver det ett vinterpaket med extra golvisolering, en investering på 8 000–15 000 kr som ofta betalar sig i minskad kondens.",
    ],
    faq: [
      {
        q: "Räcker B-körkort till en Adria Matrix?",
        a: "Ja om totalvikten är högst 3 500 kg, vilket de flesta Matrix-modeller är. Kontrollera registreringsbeviset och räkna på din faktiska last.",
      },
      {
        q: "Hur mycket kan man lasta i en Matrix?",
        a: "Vanligen 300–500 kg beroende på utrustningsnivå. Full vattentank på 100 liter tar 100 kg av den budgeten.",
      },
      {
        q: "Vad kostar en begagnad Adria Matrix?",
        a: "Typiska spann är 480 000–650 000 kr för 2016–2018 och 620 000–830 000 kr för 2019–2021.",
      },
    ],
  },
  {
    brandSlug: "adria",
    brandName: "Adria",
    category: "husbil",
    slug: "coral",
    name: "Adria Coral",
    h1: "Adria Coral — rymlig husbil för längre resor",
    metaTitle: "Adria Coral begagnad: pris, planlösningar och vad vi kollar",
    metaDesc:
      "Adria Coral begagnat 2017–2023: prisspann, planlösningar med ö-säng och garage, vinterduglighet och vår 12-punkters kontroll före köp.",
    keyword: "adria coral",
    intro:
      "Coral är Adrias större husbilsserie där boytan prioriteras. Den passar dig som reser långt och bor i bilen i veckor snarare än helger.",
    layouts: [
      "Ö-säng bak med stort våtrum och genomgående garage",
      "Enkelsängar bak med separat dusch",
      "Nedsänkbar säng fram plus fast bädd bak för fyra personer",
    ],
    perfectFor: [
      "Längre Europaresor där boytan betyder mer än ytterlängden",
      "Par som vill kunna gå runt sängen och ha riktig dusch",
      "Den som behöver garage för elcyklar med hög lastvikt",
    ],
    strengths: [
      "Generös boyta och bra ståhöjd genom hela bilen",
      "Stor kyl och gott om köksförvaring för långresa",
      "Genomgående garage som tar två elcyklar",
      "Välfungerande värmefördelning för trekvartsårsbruk",
    ],
    weaknesses: [
      "Ytterlängd på 7–7,5 meter kräver planering vid parkering",
      "Ofta 3 500 kg totalvikt med begränsad lastmarginal",
      "Bränsleförbrukningen ligger 1–2 liter över mindre halvintegrerade",
    ],
    winter:
      "Coral fungerar bra i vår- och höstkyla. För vinterbruk i Norrbotten rekommenderar vi vinterpaket, hyttisolering och att avloppstanken hålls uppvärmd eller töms löpande.",
    priceNew: "Från cirka 950 000 kr",
    priceUsed:
      "2017–2018: 550 000–700 000 kr · 2019–2021: 680 000–890 000 kr · 2022–2023: 850 000–1 150 000 kr",
    vanligaFel: [
      "Fukt i garagegolv där blöt utrustning stått utan torkning",
      "Slitna tätningar kring stora servicelucker efter 7 år",
      "Sprickbildning i tätmassa runt takmonterad utrustning",
      "Nedsatt batterikapacitet vid mycket frikoppling utan solceller",
    ],
    checklist: [
      "Fuktmätning i 12 punkter inklusive garage, hjulhus och takgenomföringar",
      "Vägning av bilen för att fastställa verklig lastmarginal",
      "Genomgång av chassiservice enligt tillverkarens intervall",
      "Test av värmepanna, varmvatten och golvvärme där sådan finns",
      "Kontroll av markis, stödben och backkamera",
    ],
    djup: [
      "Coral är byggd för den som bor i bilen. Ståhöjden och den genomgående mittgången gör att två personer kan röra sig förbi varandra utan att någon behöver sätta sig, och det är precis den detaljen som avgör hur en tre veckor lång resa upplevs. Köket har arbetsyta nog för riktig matlagning, inte bara uppvärmning.",
      "Storleken har ett pris i praktisk hantering. Med 7–7,5 meter ytterlängd behöver du tänka på färjeavgifter, campingplatsernas rutstorlek och parkering i stadskärnor. Vi brukar rekommendera att man provkör bilen i en trång rondell och backar in på en normal ruta innan köpet — det säger mer än vilken broschyr som helst.",
      "Lastmarginalen är också värd att räkna på. Med 3 500 kg totalvikt och en tjänstevikt kring 3 100–3 300 kg blir marginalen 200–400 kg. Två elcyklar väger 50–60 kg, vattentanken 100 kg och packning för två personer 80–120 kg. Där tar det snabbt slut, och därför finns chassivarianter med högre totalvikt som kräver C1-behörighet.",
      "Vi ser Coral som en utmärkt trekvartsårsbil och en fullt användbar vinterbil med rätt utrustning. Den enskilt viktigaste åtgärden är att hantera avloppsvattnet: en tank som fryser mitt i en kall vecka är det vanligaste vinterproblemet vi rycker ut på, och det löses enkelt med uppvärmd tank eller rutin att tömma varje dag.",
    ],
    faq: [
      {
        q: "Vad skiljer Adria Coral från Matrix?",
        a: "Coral är större och prioriterar boyta, Matrix är kortare och lättare att parkera. Coral har ofta större våtrum och garage.",
      },
      {
        q: "Är Adria Coral svår att köra?",
        a: "Nej, men längden märks vid parkering och i trånga stadskärnor. Backkamera och provkörning före köp rekommenderas.",
      },
      {
        q: "Hur mycket drar en Coral?",
        a: "Räkna med cirka 1,1–1,4 liter per mil i landsvägsfart beroende på lastvikt, vind och hastighet.",
      },
    ],
  },
  {
    brandSlug: "hymer",
    brandName: "Hymer",
    category: "husbil",
    slug: "exsis",
    name: "Hymer Exsis",
    h1: "Hymer Exsis — lätt husbil med premiumkänsla",
    metaTitle: "Hymer Exsis begagnad: pris, vikt och vad vi kontrollerar",
    metaDesc:
      "Hymer Exsis begagnat: prisspann per årsmodell, låg vikt och lastmarginal, vinteregenskaper i norrländskt klimat och vår kontroll före köp.",
    keyword: "hymer exsis",
    intro:
      "Exsis är Hymers lätta serie och ett svar på ett vanligt problem: att husbilar blir för tunga för sin totalvikt. Här får du premiumbygge med lastmarginal kvar.",
    layouts: [
      "Enkelsängar bak med kompakt garage",
      "Fransk säng bak med mittvåtrum",
      "Nedsänkbar säng fram för fyra sovplatser",
    ],
    perfectFor: [
      "Par som vill ha B-körkort och verklig lastmarginal",
      "Den som prioriterar byggkvalitet och tystnad i kupén",
      "Långresenärer som vill hålla bränsleförbrukningen nere",
    ],
    strengths: [
      "Låg tjänstevikt ger ofta 400–600 kg lastmarginal",
      "PUAL-konstruktionen är okänslig för fukt i väggen",
      "Hög materialkvalitet i inredning och beslag",
      "Bra isolering i förhållande till bilens vikt",
    ],
    weaknesses: [
      "Högre inköpspris än volymmärken i samma storlek",
      "Färre begagnade exemplar på svenska marknaden",
      "Vissa reservdelar tar längre tid att få hem, ofta 5–14 dagar",
    ],
    winter:
      "Exsis håller värmen bra för sin vikt och fungerar ner mot -15 grader utan särskilda åtgärder. Hyttisolering och ett golvunderlägg lyfter komforten ytterligare vid längre vinterperioder.",
    priceNew: "Från cirka 1 050 000 kr",
    priceUsed:
      "2015–2017: 520 000–700 000 kr · 2018–2020: 680 000–900 000 kr · 2021–2023: 880 000–1 250 000 kr",
    vanligaFel: [
      "Tätningar kring takluckor som behöver ses över vid 7–8 år",
      "Slitage på förarstolarnas vridmekanism efter mycket användning",
      "Kondens i förarhytten vid vinterbruk utan hyttgardin",
      "Åldrade servicebatterier på bilar som stått oanvända över vintern",
    ],
    checklist: [
      "Fuktmätning i 12 punkter — PUAL ger normalt låga värden, avvikelser är signal",
      "Kontroll av verklig lastmarginal mot registrerad totalvikt",
      "Genomgång av chassiservice och kamrem enligt intervall",
      "Test av värmesystem, varmvatten och elsystem i minst 30 minuter",
      "Kontroll av däckålder, CP-märkning och lufttryck per axel",
    ],
    djup: [
      "Hymers PUAL-konstruktion är det tekniska argument som håller bäst över tid. Genom att bygga vägg och tak i material som inte suger vatten minskar risken för de fuktskador som annars är husbilsägarens största enskilda kostnad. Vi ser det i mätprotokollen: Hymer-bilar i tioårsåldern har oftare värden under 12 procent än jämnåriga bilar med traditionell träregelstomme.",
      "Viktfrågan är seriens själva idé. Många halvintegrerade i 7-metersklassen har 200–300 kg kvar att lasta, medan en Exsis ofta ger 400–600 kg. I praktiken betyder det att du kan ha full vattentank, två cyklar och packning för en månad utan att ligga på gränsen — och att bilen kör och bromsar som den ska.",
      "Prisbilden är högre än volymmärkena, men värdeminskningen är flackare. Räkna med 8–10 procent per år de första fyra åren mot 12–14 procent för volymmärken. Över en sjuårsperiod jämnar det ut en betydande del av prisskillnaden, särskilt om du säljer vidare i stället för att köra bilen tills den är utsliten.",
      "På den svenska begagnatmarknaden är utbudet tunnare än för Adria och Dethleffs. Det innebär att den som letar en specifik planlösning behöver ha framförhållning. Lägg en prisvakt hos oss så hör vi av oss när ett exemplar kommer in i inbyte, ofta innan det annonseras ut brett.",
    ],
    faq: [
      {
        q: "Varför är Hymer Exsis dyrare än liknande husbilar?",
        a: "Du betalar för PUAL-konstruktionen, lägre vikt och högre materialkvalitet. Det syns i lägre fuktrisk och flackare värdeminskning.",
      },
      {
        q: "Hur mycket kan man lasta i en Exsis?",
        a: "Ofta 400–600 kg, vilket är märkbart mer än många konkurrenter i samma storleksklass.",
      },
      {
        q: "Klarar Hymer Exsis svensk vinter?",
        a: "Ja, ner mot -15 grader utan särskilda åtgärder. Hyttgardin och golvunderlägg rekommenderas vid längre vinterbruk.",
      },
    ],
  },
  {
    brandSlug: "dethleffs",
    brandName: "Dethleffs",
    category: "husbil",
    slug: "trend",
    name: "Dethleffs Trend",
    h1: "Dethleffs Trend — mycket husbil för pengarna",
    metaTitle: "Dethleffs Trend begagnad: pris, planlösningar och vanliga fel",
    metaDesc:
      "Dethleffs Trend begagnat: prisspann per årsmodell, planlösningar för familj och par, vinterduglighet och vår checklista före köp.",
    keyword: "dethleffs trend",
    intro:
      "Trend är Dethleffs bredaste husbilsserie och byggd för familjer. Många planlösningar, gott om sovplatser och en prisnivå som gör att fler kommer in i husbilslivet.",
    layouts: [
      "Våningssäng bak plus nedsänkbar säng fram — upp till sex sovplatser",
      "Enkelsängar bak med garage",
      "Fransk säng bak med kompakt våtrum",
    ],
    perfectFor: [
      "Familjer med två eller tre barn som behöver fasta sovplatser",
      "Den som vill ha mycket utrymme per investerad krona",
      "Semesterresor i Europa under sommar och tidig höst",
    ],
    strengths: [
      "Flest sovplatser per meter i sitt segment",
      "Rejält byggda möbler som tål familjeslitage",
      "Bra prisnivå både nytt och begagnat",
      "Stort återförsäljarnät i Europa gör delar lätta att få tag på",
    ],
    weaknesses: [
      "Full bemanning gör att lastmarginalen tar slut snabbt",
      "Standardisoleringen räcker inte för djupvinter i Norrbotten",
      "Värdeminskning kring 12–14 % per år de första åren",
    ],
    winter:
      "Trend är i grunden en trekvartsårsbil. Med vinterpaket, hyttisolering och rutin på avloppstanken fungerar den i svensk vinter, men för uppställning i djup kyla rekommenderar vi en bil med dubbelt golv.",
    priceNew: "Från cirka 820 000 kr",
    priceUsed:
      "2016–2018: 450 000–620 000 kr · 2019–2021: 590 000–800 000 kr · 2022–2023: 780 000–1 020 000 kr",
    vanligaFel: [
      "Slitna stegar och beslag vid våningssängar i familjebilar",
      "Fukt i garaget där våta kläder och utrustning förvarats",
      "Läckage vid takluckor på bilar äldre än 7 år",
      "Trötta servicebatterier vid mycket frikoppling utan solceller",
    ],
    checklist: [
      "Fuktmätning i 12 punkter med extra fokus på garage och takgenomföringar",
      "Vägning för att fastställa lastmarginal med full familj ombord",
      "Kontroll av samtliga sovplatsers infästningar och bältade platser",
      "Genomgång av chassiservice och bromsar",
      "Test av kyl, värme och varmvatten i minst 30 minuter",
    ],
    djup: [
      "Trend är den husbil vi oftast rekommenderar till barnfamiljer, av en enkel anledning: fasta sovplatser till alla. Att slippa bädda om varje kväll är skillnaden mellan en semester och ett projekt när man reser med tre barn. Våningssängarna bak är dessutom bältade i flera utföranden, vilket ger fler lagliga passagerarplatser.",
      "Med familj följer vikt. Fem personer väger 300–350 kg, packning för två veckor 100–150 kg och full vattentank 100 kg. Det är över 500 kg innan cyklar och campingmöbler kommer ombord, och därför är vägning inte en formalitet utan avgörande. Många familjer behöver ett chassi med 3 850 eller 4 500 kg totalvikt, vilket i sin tur påverkar körkortsbehörigheten.",
      "Prisbilden är seriens stora styrka. Du får ofta 30–50 procents mer sovkapacitet per krona än i en premiumbil, och begagnatutbudet är stort tack vare höga försäljningsvolymer i Europa. Värdeminskningen på 12–14 procent per år de första åren gör att en tre till fyra år gammal bil ofta är den bästa affären.",
      "I verkstaden i Kalix ser vi framför allt slitagefel på familjebilarna, inte konstruktionsfel: stegar, gångjärn, lucklås och tätningar som fått ta emot mycket. Det är billiga åtgärder om de tas i tid, men dyra om fukt hunnit gå in i en skarv. Årlig tätningskontroll är därför den bästa försäkringen i just den här serien.",
    ],
    faq: [
      {
        q: "Hur många sovplatser har en Dethleffs Trend?",
        a: "Beroende på planlösning fyra till sex, varav flera fasta. Kontrollera antalet bältade platser separat.",
      },
      {
        q: "Räcker 3 500 kg totalvikt för en familj?",
        a: "Ofta inte. Med fem personer, packning och vatten behövs vanligen ett tyngre chassi, vilket kan kräva C1-behörighet.",
      },
      {
        q: "Passar Trend för vintercamping?",
        a: "För kortare vinterresor ja, med vinterpaket. För uppställning i djup kyla rekommenderar vi en bil med dubbelt golv.",
      },
    ],
  },
];

export const MODELL_SERIER: ModellSerie[] = [...MODELL_SERIER_1, ...MODELL_SERIER_2];

export const MODELL_SERIE_SLUGS = MODELL_SERIER.map((m) => `${m.brandSlug}/${m.slug}`);

export function getModellSerie(brand: string, serie: string): ModellSerie | undefined {
  return MODELL_SERIER.find(
    (m) => m.brandSlug === brand.toLowerCase() && m.slug === serie.toLowerCase(),
  );
}

/** All unik text på sidan — underlag för kvalitetsgrinden. */
export function modellSerieText(m: ModellSerie): string {
  return [
    m.h1,
    m.intro,
    ...m.layouts,
    ...m.perfectFor,
    ...m.strengths,
    ...m.weaknesses,
    m.winter,
    m.priceNew,
    m.priceUsed,
    ...m.vanligaFel,
    ...m.checklist,
    ...m.djup,
    ...m.faq.flatMap((f) => [f.q, f.a]),
  ].join(" ");
}
